/**
 * orderSimulationEngine.js — Re Leve IA (Etapa 6)
 * Simula confirmação de kit e geração de resumo para WhatsApp.
 * Sem venda real, sem WhatsApp real, sem backend, sem banco.
 */

import { addSessionEvent, getSessionEvents } from './localStorageEngine';
import { hasCalorieConsent } from './consentEngine';
import { getDeclaredRestrictions } from './restrictionEngine';
import { createCommercialEvent, COMMERCIAL_EVENT_TYPES } from './commercialJourneyEngine';

const CHECKOUT_KEY_PREFIX = 'releve_ia_checkout_';

function storageSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // fallback silencioso
  }
}

function storageGet(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function storageRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch { /* ignore */ }
}

// ── Confirmar seleção de kit ───────────────────────────────────────────────────

export function confirmKitSelection({ clienteId, customer, kit, source }) {
  if (!kit) return null;

  const kitName = kit.kitName || kit.nome || 'Kit Re Leve';
  const kitId   = kit.kitId || kit.id || null;

  // 1. Criar evento comercial
  createCommercialEvent({
    clienteId,
    type: COMMERCIAL_EVENT_TYPES.KIT_CONFIRMED,
    source: source || 'chatbot',
    payload: { kitId, kitName, timestamp: new Date().toISOString() },
  });

  // 2. Estado do checkout simulado
  const checkoutState = {
    clienteId,
    kitId,
    kitName,
    confirmedAt: new Date().toISOString(),
    source: source || 'chatbot',
    status: 'confirmed',
    customer: {
      segmento: customer?.segmentoComercial || customer?.segmento || 'desconhecido',
    },
  };

  storageSet(CHECKOUT_KEY_PREFIX + clienteId, checkoutState);

  // 3. Verificar consentimento e restrições
  const calorieConsent = clienteId ? hasCalorieConsent(clienteId) : false;
  const restrictions   = clienteId ? getDeclaredRestrictions(clienteId) : [];

  // 4. Gerar resumo
  const summary = buildWhatsAppSummary({ clienteId, customer, kit: { kitId, kitName }, restrictions, calorieConsent });

  return {
    ...checkoutState,
    calorieConsent,
    restrictions,
    whatsappSummary: summary,
  };
}

// ── Montar resumo para WhatsApp simulado ──────────────────────────────────────

export function buildWhatsAppSummary({ clienteId, customer, kit, restrictions, calorieConsent }) {
  const kitName = kit?.kitName || kit?.nome || 'Kit Re Leve';
  const hasRestriction = restrictions && restrictions.length > 0;
  const restrictionLabels = hasRestriction
    ? restrictions.map(r => r.label || r.type).join(', ')
    : null;

  let text = `Olá! Quero montar o kit *${kitName}*. Tenho interesse em finalizar pelo WhatsApp.`;

  if (hasRestriction) {
    text += `\n\n⚠️ Observação: informei restrição alimentar (${restrictionLabels}) e gostaria de confirmar disponibilidade.`;
  }

  if (calorieConsent) {
    text += `\n\n📊 Também autorizei acompanhamento calórico informativo para registrar os itens Re Leve.`;
  }

  return {
    text,
    kitName,
    hasRestriction,
    restrictionLabels,
    calorieConsent,
    // link simulado — sem telefone real
    whatsappHref: '#whatsapp-simulado',
    disclaimer: 'Resumo simulado para protótipo. WhatsApp real será integrado na próxima etapa.',
  };
}

// ── Estado do checkout simulado ───────────────────────────────────────────────

export function getSimulatedCheckoutState(clienteId) {
  if (!clienteId) return null;
  return storageGet(CHECKOUT_KEY_PREFIX + clienteId);
}

// ── Limpar checkout simulado ──────────────────────────────────────────────────

export function clearSimulatedCheckout(clienteId) {
  if (!clienteId) return;
  storageRemove(CHECKOUT_KEY_PREFIX + clienteId);
}
